import type { BuyerBiographySaveOutcome } from "@/api/buyers";

/**
 * The sentences the biography paste flow says about a READ or a SAVE: what went
 * wrong with a read, why an empty biography cannot be saved, and what a save
 * actually did.
 *
 * <p>Two of these are the server's own sentences and are pinned to the C# by
 * `combinedIntro/combinedIntroCopy.parity.test.ts`: a read that comes back empty is the failure
 * the server names "nothing found", and a biography with nothing in it is the
 * save the server refuses.</p>
 *
 * <p>This is NOT all of the flow's copy. The paste step's paragraphs, both form
 * captions and the control labels — "On file", "Keep what's saved", "Kept from
 * what's saved", "Keep everything the paste doesn't mention" and "Fill in the
 * fields by hand instead" — are written inline in `BuyerBiographyPanel.tsx`.</p>
 */

/** A read that found no biography — identical whether it arrives as the
 *  server's 422 or as a 200 with every field empty. */
export const BIOGRAPHY_NOTHING_FOUND_MESSAGE =
  "We couldn't find a buyer's biography in this text. Check that it's about the buyer, or fill in the fields by hand.";

/** A read that failed for a reason the server did not name — a 5xx or a
 *  network failure. The server's own 500 text is for the log. */
export const BIOGRAPHY_ANALYSE_FAILED_MESSAGE =
  "Something went wrong analysing this. Your paste is kept. Try again, or fill in the fields by hand.";

/** Why Save is disabled on an all-empty biography; the server's refusal of one. */
export const BIOGRAPHY_EMPTY_MESSAGE =
  "A biography can't be saved empty. Fill in at least one field, then save.";

/**
 * What a save says, built from what the SERVER says it did.
 *
 * <p>🔴 A save that changed nothing writes nothing and does not re-stamp the
 * review, and says so. Every save used to announce the same success, which is
 * how a paste that changed nothing looked like one that worked.</p>
 */
export const BIOGRAPHY_SAVE_ANNOUNCEMENTS: Record<BuyerBiographySaveOutcome, string> = {
  Unchanged: "No changes — nothing was saved.",
  SourceTextOnly: "Biography marked reviewed — no fields changed.",
  FieldsChanged: "Biography saved and marked reviewed.",
};
